import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FilterDropdown from '../../components/common/FilterDropdown';
import { GenericView, SearchInput, ActionButtons } from '../../components/common/table';
import TemplateForm from './components/TemplateForm';
import ViewTemplate from './components/ViewTemplate';
import Alert from '../../components/Alert/Alert';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import api from '../../api'; // Added import for api
import PizZip from 'pizzip';
import Docxtemplater from 'docxtemplater';
import ImageModule from 'docxtemplater-image';
import { saveAs } from 'file-saver';

const CreateTemplate = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterData, setFilterData] = useState({});
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateData, setTemplateData] = useState([]);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Tambahan: untuk info dokumen
  const [documentInfo, setDocumentInfo] = useState(null);

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000); // Auto hide after 5 seconds
  };

  const handleFilterChange = (name, value) => {
    setFilterData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilterReset = () => {
    setFilterData({
      dateFrom: '',
      dateTo: '',
      client: '',
      project: '',
      status: ''
    });
  };

  const handleFilterApply = () => {
    // Add your filter logic here
  };

  const columns = [
    {
      key: 'templateName',
      label: 'Template Name'
    },
    {
      key: 'serviceName', // Changed from 'service' to 'serviceName'
      label: 'Service'
    },
    {
      key: 'createdAt',
      label: 'Created At',
      type: 'date'
    },
    {
      key: 'updatedAt',
      label: 'Last Modified',
      type: 'date'
    }
  ];

  // Update fetchTemplates function to use API
  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await api.get('/crm/proposal-templates');
      setTemplateData(response.data || []);
    } catch (err) {
      console.error('Error fetching templates:', err);
      showAlert('danger', 'Failed to fetch templates.');
      setTemplateData([]);
    } finally {
      setLoading(false);
    }
  };

  // Update useEffect to call fetchTemplates
  useEffect(() => {
    fetchTemplates();
  }, []);

  // Update: ketika memilih template, simpan info dokumen
  const handleTemplateSelect = (template) => {
    setActiveTemplate(template);
    setShowEditor(true);
    setUploadedFile(null);
    setDocumentInfo({
      name: template.documentFileName,
      size: template.documentFileSize,
      id: template.id,
    });
  };

  // Upload dokumen ke endpoint baru
  const handleSaveTemplate = async () => {
    if (!activeTemplate) {
      showAlert('danger', 'Please select a template first.');
      return;
    }
    if (!uploadedFile) {
      showAlert('danger', 'Please upload a .docx template file.');
      return;
    }
    setLoading(true);
    setAlert(null);
    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);

      await api.post(`/crm/proposal-templates/${activeTemplate.id}/upload-document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      showAlert('success', 'Template document uploaded successfully!');
      await fetchTemplates();
      setUploadedFile(null);
      setActiveTemplate(null);
      setShowEditor(false);
    } catch (err) {
      showAlert('danger', err.response?.data?.message || 'Failed to upload template document.');
    } finally {
      setLoading(false);
    }
  };

  // Download dokumen
  const handleDownloadDocument = async () => {
    if (!activeTemplate?.id) return;
    try {
      const response = await api.get(`/crm/proposal-templates/${activeTemplate.id}/download-document`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', activeTemplate.documentFileName || 'template.docx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      showAlert('danger', 'Failed to download document.');
    }
  };

  // Hapus dokumen
  const handleDeleteDocument = async () => {
    if (!activeTemplate?.id) return;
    try {
      await api.delete(`/crm/proposal-templates/${activeTemplate.id}/document`);
      showAlert('success', 'Document deleted.');
      setDocumentInfo(null);
      await fetchTemplates();
    } catch (err) {
      showAlert('danger', 'Failed to delete document.');
    }
  };

  // Update handleFormSubmit
  const handleFormSubmit = async (formData) => {
    setLoading(true);
    setAlert(null);
    
    try {
      let response;
      
      const payload = {
        templateName: formData.templateName,
        serviceId: formData.service,
        serviceName: formData.serviceName,
        templateContent: formData.templateContent,
        createdBy: 'current-user', // Get from auth context
        updatedBy: 'current-user'  // Get from auth context
      };
      
      if (editingTemplate) {
        // Update existing template
        response = await api.put(`/crm/proposal-templates/${editingTemplate.id}`, payload);
        showAlert('success', 'Template updated successfully!');
      } else {
        // Create new template
        response = await api.post('/crm/proposal-templates', payload);
        showAlert('success', 'Template created successfully!');
      }
      
      // Refresh the data
      await fetchTemplates();
      
      // Close modal and reset form
      setIsModalOpen(false);
      setEditingTemplate(null);
      
    } catch (err) {
      console.error('Error saving template:', err);
      showAlert('danger', err.response?.data?.message || 'Failed to save template.');
    } finally {
      setLoading(false);
    }
  };

  // Add function to handle create new template button
  const handleCreateNewTemplate = () => {
    // Reset editing state
    setEditingTemplate(null);
    // Open modal
    setIsModalOpen(true);
  };

  // Update handleEdit to properly set editing state
  const handleEdit = async (row) => {
    try {
      const response = await api.get(`/crm/proposal-templates/${row.id}`);
      setEditingTemplate(response.data);
      setIsModalOpen(true);
    } catch (err) {
      console.error('Error fetching template details:', err);
      showAlert('danger', 'Failed to load template details.');
    }
  };

  // Update handleDelete
  const handleDelete = async (row) => {
    setItemToDelete(row);
    setShowDeleteModal(true);
  };

  // Update handleView to fetch template data
  const handleView = async (row) => {
    try {
      const response = await api.get(`/crm/proposal-templates/${row.id}`);
      setSelectedTemplate(response.data);
      setIsViewModalOpen(true);
    } catch (err) {
      console.error('Error fetching template details:', err);
      showAlert('danger', 'Failed to load template details.');
    }
  };

  // Dummy data for placeholders
  const DUMMY_DATA = {
    // General Information
    company_name: 'PT Dummy Client Indonesia',
    company_address: 'Jl. Sudirman No. 123, Jakarta Selatan 12190',
    company_pic: 'Yadi Mulyadi',
    
    // Proposal Details
    standard: 'ISO 9001:2015',
    STANDARD: 'ISO 9001:2015', // ✅ TAMBAHAN: Support untuk placeholder huruf besar
    standard_scheme: 'Quality Management System',
    STANDARD_SCHEME: 'Quality Management System', // ✅ TAMBAHAN: Support untuk placeholder huruf besar
    scope: 'Manufacturing of Electronic Components',
    SCOPE: 'Manufacturing of Electronic Components', // ✅ TAMBAHAN: Support untuk placeholder huruf besar
    proposal_number: 'PROP-2024-001',
    proposal_date: '2024-06-01',
    project_name: 'ISO 9001:2015 Certification Project',
    
    // PIC Contact Info
    pic_name: 'Yadi Mulyadi',
    pic_phone: '+62 812-3456-7890',
    pic_email: 'yadi.mulyadi@dummyclient.com',
    pic_position: 'Project Manager',
    
    // Sales Contact Info
    sales_name: 'John Sales Manager',
    sales_phone: '+62 812-9876-5432',
    sales_email: 'john.sales@company.com',
    sales_position: 'Senior Sales Manager',
    
    // Signatures
    sales_signature: '/static/signature.png',
    pic_signature: '/static/signature.png',
    
    // Dynamic service items with years array for dash syntax (5 years)
    service_items: [
      {
        service_name: 'Certification Management Fee',
        years: [
          { year: '2024', amount: '2,000,000' },
          { year: '2025', amount: '2,000,000' },
          { year: '2026', amount: '2,000,000' },
          { year: '2027', amount: '2,000,000' },
          { year: '2028', amount: '2,000,000' }
        ]
      },
      {
        service_name: 'Initial Audit (Stage 1 + Stage 2)',
        years: [
          { year: '2024', amount: '5,000,000' },
          { year: '2025', amount: '0' },
          { year: '2026', amount: '0' },
          { year: '2027', amount: '0' },
          { year: '2028', amount: '0' }
        ]
      },
      {
        service_name: 'Surveillance I',
        years: [
          { year: '2024', amount: '0' },
          { year: '2025', amount: '3,000,000' },
          { year: '2026', amount: '0' },
          { year: '2027', amount: '0' },
          { year: '2028', amount: '0' }
        ]
      },
      {
        service_name: 'Surveillance II',
        years: [
          { year: '2024', amount: '0' },
          { year: '2025', amount: '0' },
          { year: '2026', amount: '3,000,000' },
          { year: '2027', amount: '0' },
          { year: '2028', amount: '0' }
        ]
      },
      {
        service_name: 'Surveillance III',
        years: [
          { year: '2024', amount: '0' },
          { year: '2025', amount: '0' },
          { year: '2026', amount: '0' },
          { year: '2027', amount: '3,000,000' },
          { year: '2028', amount: '0' }
        ]
      },
      {
        service_name: 'Recertification',
        years: [
          { year: '2024', amount: '0' },
          { year: '2025', amount: '0' },
          { year: '2026', amount: '0' },
          { year: '2027', amount: '0' },
          { year: '2028', amount: '8,000,000' }
        ]
      },
      {
        service_name: 'Additional Training',
        years: [
          { year: '2024', amount: '1,500,000' },
          { year: '2025', amount: '1,500,000' },
          { year: '2026', amount: '1,500,000' },
          { year: '2027', amount: '1,500,000' },
          { year: '2028', amount: '1,500,000' }
        ]
      },
      {
        service_name: 'Documentation Support',
        years: [
          { year: '2024', amount: '800,000' },
          { year: '2025', amount: '400,000' },
          { year: '2026', amount: '400,000' },
          { year: '2027', amount: '400,000' },
          { year: '2028', amount: '800,000' }
        ]
      }
    ],
    
    // Dynamic years structure (5 years)
    years: [
      {
        year: '2024',
        year_total: '10,000,000'
      },
      {
        year: '2025',
        year_total: '7,400,000'
      },
      {
        year: '2026',
        year_total: '5,900,000'
      },
      {
        year: '2027',
        year_total: '5,900,000'
      },
      {
        year: '2028',
        year_total: '12,300,000'
      }
    ],
    
    grand_total: '41,500,000'
  };

  // Helper to fill uploaded docx with dummy data
  async function fillDocxWithDummyData(file) {
    const arrayBuffer = await file.arrayBuffer();
    const zip = new PizZip(arrayBuffer);
    
    // Pre-load the signature images
    let salesSignatureBuffer = null;
    let picSignatureBuffer = null;
    try {
      const salesSignatureResponse = await fetch('/static/signature.png');
      salesSignatureBuffer = await salesSignatureResponse.arrayBuffer();
      
      const picSignatureResponse = await fetch('/static/signature.png');
      picSignatureBuffer = await picSignatureResponse.arrayBuffer();
    } catch (error) {
      console.warn('Could not load signature images:', error);
    }
    
    // Configure image module for signature images only
    const imageOpts = {
      centered: false, // Set to true to always center images
      fileType: "docx",
      getImage: function(tagValue, tagName) {
        // Handle signature images
        if (tagName === 'sales_signature') {
          // Return the pre-loaded sales signature image buffer
          if (salesSignatureBuffer) {
            return salesSignatureBuffer;
          }
          return null;
        } else if (tagName === 'pic_signature') {
          // Return the pre-loaded PIC signature image buffer
          if (picSignatureBuffer) {
            return picSignatureBuffer;
          }
          return null;
        }
        // For other image types, return null
        return null;
      },
      getSize: function(img, tagValue, tagName) {
        // Return fixed size for signature images
        if (tagName === 'sales_signature' || tagName === 'pic_signature') {
          return [200, 100]; // width, height
        }
        return [200, 100]; // default size
      }
    };
    
    const imageModule = new ImageModule(imageOpts);
    const doc = new Docxtemplater(zip, { 
      paragraphLoop: true, 
      linebreaks: true,
      modules: [imageModule] // Attach the image module
    });
    
    doc.setData(DUMMY_DATA);
    
    // ✅ DEBUG: Log data yang akan digunakan untuk filling
    console.log('=== DEBUG: DUMMY_DATA untuk template filling ===');
    console.log('standard:', DUMMY_DATA.standard);
    console.log('standard_scheme:', DUMMY_DATA.standard_scheme);
    console.log('scope:', DUMMY_DATA.scope);
    console.log('Full DUMMY_DATA:', DUMMY_DATA);
    
    try {
      // ✅ DEBUG: Cek template variables yang ada di dokumen
      const templateVariables = doc.getFullText();
      console.log('=== DEBUG: Template variables found ===');
      console.log('Template text:', templateVariables);
      
      // Cari semua placeholder dalam template
      const placeholders = templateVariables.match(/\{[^}]+\}/g);
      console.log('Found placeholders:', placeholders);
      
      // Cek khusus untuk standard placeholder
      const standardPlaceholders = templateVariables.match(/\{standard\}/g);
      console.log('Standard placeholders found:', standardPlaceholders);
      
      doc.render();
      console.log('✅ Template rendered successfully');
    } catch (error) {
      console.error('❌ Error filling template:', error);
      console.error('Error details:', {
        message: error.message,
        name: error.name,
        stack: error.stack
      });
      showAlert('danger', 'Error filling template: ' + error.message);
      return;
    }
    const out = doc.getZip().generate({ type: 'blob', mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
    saveAs(out, 'filled_template.docx');
  }

  return (
    <>
      <div className="container-xl">
        {/* Alert Component */}
        {alert && (
          <Alert
            type={alert.type}
            message={alert.message}
            onClose={() => setAlert(null)}
          />
        )}

        <div className="row g-2 align-items-center">
          <div className="col">
            <h3 className="page-title m-0">Create Template</h3>
          </div>
          <div className="col-auto ms-auto d-print-none">
            <div className="btn-list">
              <button 
                className="btn btn-primary"
                onClick={handleCreateNewTemplate} // Changed from setIsModalOpen(true)
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-plus" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                  <path d="M12 5l0 14"></path>
                  <path d="M5 12l14 0"></path>
                </svg>
                Create
              </button>
            </div>
          </div>
        </div>

        <TemplateForm
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setEditingTemplate(null); // Reset editing state when closing
          }}
          onSubmit={handleFormSubmit}
          editingTemplate={editingTemplate} // Pass editing template
          loading={loading}
          alert={alert}
        />

        <ViewTemplate
          isOpen={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          data={selectedTemplate}
        />

        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setItemToDelete(null);
          }}
          onConfirm={async () => {
            if (!itemToDelete?.id) return;
            setLoading(true);
            setAlert(null);
            try {
              await api.delete(`/crm/proposal-templates/${itemToDelete.id}`);
              showAlert('success', 'Template deleted successfully!');
              await fetchTemplates();
            } catch (err) {
              showAlert('danger', err.response?.data?.message || 'Failed to delete template.');
            } finally {
              setLoading(false);
            }
            setShowDeleteModal(false);
            setItemToDelete(null);
          }}
          title="Delete Template"
          message={`Are you sure you want to delete this? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          confirmVariant="danger"
        />

        <div className="page-body">
          <div className="container-xl">
            <div className="col">
              <div className="card">
                <div className="card-header">
                  <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs" role="tablist">
                    <li className="nav-item-tab" role="presentation">
                      <a href="#tabs-list-data" className="nav-link active" data-bs-toggle="tab" aria-selected="true" role="tab">List Data</a>
                    </li>
                    <li className="nav-item-tab" role="presentation">
                      <a href="#tabs-template-editor" className="nav-link" data-bs-toggle="tab" aria-selected="false" tabIndex="-1" role="tab">Template Editor</a>
                    </li>
                  </ul>
                </div>
                <div className="card-body">
                  <div className="tab-content">
                    <div className="tab-pane active show" id="tabs-list-data" role="tabpanel">
                      <GenericView
                        data={templateData}
                        columns={columns}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onView={handleView}
                        filterComponent={
                          <FilterDropdown
                            filterData={filterData}
                            onFilterChange={handleFilterChange}
                            onReset={handleFilterReset}
                            onApply={handleFilterApply}
                          />
                        }
                        searchComponent={
                          <SearchInput
                            value={searchQuery}
                            onChange={setSearchQuery}
                            placeholder="Search templates..."
                          />
                        }
                        actionButtons={
                          <ActionButtons
                            onExportExcel={() => {
                              showAlert('info', 'Export to Excel functionality coming soon');
                            }}
                            onExportPDF={() => {
                              showAlert('info', 'Export to PDF functionality coming soon');
                            }}
                          />
                        }
                      />
                    </div>
                    
                    <div className="tab-pane" id="tabs-template-editor" role="tabpanel">
                      <div className="row">
                        <div className="col-12 col-md-3 border-end">
                          <div className="card-body">
                            <h4 className="subheader">Select Template</h4>
                            <hr className="my-2" />
                            <div className="list-group list-group-transparent">
                              {templateData.map(template => (
                                <a
                                  key={template.id}
                                  href="#"
                                  className={`list-group-item list-group-item-action d-flex align-items-center ${activeTemplate?.id === template.id ? 'active' : ''}`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleTemplateSelect(template);
                                  }}
                                >
                                  <div className="flex-grow-1">
                                    <div className="fw-bold">{template.templateName}</div>
                                    <small className="text-muted">{template.serviceName}</small>
                                  </div>
                                </a>
                              ))}
                              {templateData.length === 0 && (
                                <div className="text-center text-muted py-3">
                                  <small>No templates available</small>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="col-12 col-md-9">
                          <div className="card-body">
                            {showEditor ? (
                              <>
                                {/* File upload for .docx */}
                                <div className="mb-3">
                                  <label className="form-label">Upload .docx Template</label>
                                  <input
                                    type="file"
                                    accept=".docx"
                                    className="form-control"
                                    onChange={e => setUploadedFile(e.target.files[0])}
                                  />
                                </div>
                                {/* Info dokumen */}
                                {activeTemplate?.documentFileName && (
                                  <div className="mb-3">
                                    <div>
                                      <strong>Current Document:</strong> {activeTemplate.documentFileName} ({(activeTemplate.documentFileSize / 1024).toFixed(2)} KB)
                                    </div>
                                    <button className="btn btn-link" onClick={handleDownloadDocument}>Download</button>
                                    <button className="btn btn-link text-danger" onClick={handleDeleteDocument}>Delete</button>
                                  </div>
                                )}
                                {/* Placeholder info */}
                                <div className="alert alert-info">
                                  <strong>Available Placeholders:</strong>
                                  <ul className="mb-0">
                                    <li><strong>📄 General Information:</strong></li>
                                    <li>{'{company_name}'} - Company Name</li>
                                    <li>{'{company_address}'} - Company Address</li>
                                    <li>{'{company_pic}'} - Company PIC</li>
                                    <li><strong>📑 Proposal Details:</strong></li>
                                    <li><strong style={{color: 'red'}}>{'{standard}'}</strong> atau <strong style={{color: 'red'}}>{'{STANDARD}'}</strong> - Standard <span className="badge bg-success">ISO 9001:2015</span></li>
                                    <li><strong style={{color: 'red'}}>{'{standard_scheme}'}</strong> atau <strong style={{color: 'red'}}>{'{STANDARD_SCHEME}'}</strong> - Standard Scheme <span className="badge bg-success">Quality Management System</span></li>
                                    <li><strong style={{color: 'red'}}>{'{scope}'}</strong> atau <strong style={{color: 'red'}}>{'{SCOPE}'}</strong> - Scope <span className="badge bg-success">Manufacturing of Electronic Components</span></li>
                                    <li>{'{proposal_number}'} - Proposal Number</li>
                                    <li>{'{proposal_date}'} - Proposal Date</li>
                                    <li>{'{project_name}'} - Project Name</li>
                                    <li><strong>👤 PIC Contact Info:</strong></li>
                                    <li>{'{pic_name}'} - PIC Name</li>
                                    <li>{'{pic_phone}'} - PIC Phone Number</li>
                                    <li>{'{pic_email}'} - PIC Email</li>
                                    <li>{'{pic_position}'} - PIC Position</li>
                                    <li><strong>💼 Sales Contact Info:</strong></li>
                                    <li>{'{sales_name}'} - Sales Name</li>
                                    <li>{'{sales_phone}'} - Sales Phone Number</li>
                                    <li>{'{sales_email}'} - Sales Email</li>
                                    <li>{'{sales_position}'} - Sales Position</li>
                                    <li><strong>✍️ Signatures:</strong></li>
                                    <li>{'{%sales_signature}'} - Sales Signature Image</li>
                                    <li>{'{%pic_signature}'} - PIC Signature Image</li>
                                    <li><strong>📊 Dynamic Investment Table:</strong></li>
                                    <li>{'{#service_items}'} - Start loop for service items (vertical rows)</li>
                                    <li>{'{service_name}'} - Service item name (inside loop)</li>
                                    <li>{'{-w:tc years}'} - Start horizontal loop for years (table cells)</li>
                                    <li>{'{year}'} - Year value (inside year loop)</li>
                                    <li>{'{amount}'} - Amount for specific year (inside year loop)</li>
                                    <li>{'{/years}'} - End year loop</li>
                                    <li>{'{/service_items}'} - End service items loop</li>
                                    <li>{'{year_total}'} - Total for specific year</li>
                                    <li>{'{grand_total}'} - Grand total across all years</li>
                                  </ul>
                                  <small>Use these placeholders in your .docx file. Each service item has a years array with year and amount properties for proper table formatting. The signature placeholders will display images when the proposal data includes signature information.</small>
                                  <div className="mt-2 p-2 bg-warning bg-opacity-10 border border-warning rounded">
                                    <strong>🔧 Troubleshooting:</strong>
                                    <ul className="mb-0 mt-1">
                                      <li><strong>Case Sensitivity:</strong> {'{standard}'} ≠ {'{STANDARD}'} ≠ {'{Standard}'}</li>
                                      <li><strong>Supported formats:</strong> {'{standard}'}, {'{STANDARD}'}, {'{standard_scheme}'}, {'{STANDARD_SCHEME}'}</li>
                                      <li>Pastikan tidak ada spasi tambahan: {'{standard}'} bukan {'{ standard }'}</li>
                                      <li>Buka <strong>Developer Console</strong> (F12) untuk melihat debug info saat download</li>
                                      <li><strong>Jika placeholder tidak terisi:</strong> Cek log debug untuk melihat placeholder yang ditemukan vs yang tersedia</li>
                                    </ul>
                                  </div>
                                </div>
                                <div className="d-flex gap-2 mb-3">
                                  <button className="btn btn-outline-success" type="button" disabled={!uploadedFile} onClick={() => fillDocxWithDummyData(uploadedFile)}>
                                    Download Filled DOCX (with dummy data)
                                  </button>
                                </div>
                                <div className="mt-4">
                                  <button className="btn btn-primary" onClick={handleSaveTemplate} disabled={loading}>
                                    {loading ? 'Saving...' : 'Save'}
                                  </button>
                                </div>
                              </>
                            ) : (
                              <div className="text-center text-muted mt-4">
                                <p>Select a template from the list to start editing</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateTemplate; 